// @givmo/fundraisers-module — the email designer's hand edits, as changes to the email's block document
// (EMC-WEB10; D-EMC-20 revised by Jonathan, 2026-10-31 23:49–23:50: "each block of text gets text box editor … so
// the user can manually tweak a bit what the AI did").
//
// A person tweaks the AI's email in the preview: the words of a heading, a paragraph, a list item or a button,
// with bold, italic, a link and the first-name merge field; and each block's place, its removal and its photo.
// The layout and the design stay the AI's. Every hand edit is a whole new `body_json` written through the
// screen's own change route, the one the AI's change operation runs, so the renderer's rules (links, images,
// sizes, the sanitizer, the contrast floors) refuse a hand edit exactly as they refuse the AI's.
//
// Pure: the document's paths and moves, and the two-way mapping between a block's inline content (text runs
// with marks and a link, and the merge field) and the editable DOM the preview draws for it.

/** A block's place: `[3]` at the top level, `[3, 1, 0]` for the first block of the second column of block 3. */
export type BlockPath = readonly number[];

type Json = Record<string, unknown>;

function isRecord(value: unknown): value is Json {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** `"3"` or `"3.1.0"`, as the preview marks a block (`data-emc-block`), into a path; null when unreadable. */
export function parseBlockPath(mark: string | null | undefined): BlockPath | null {
  if (mark == null || !/^\d+(\.\d+\.\d+)?$/.test(mark)) return null;
  return mark.split('.').map(Number);
}

function topBlocks(body: Json): unknown[] {
  return Array.isArray(body.blocks) ? body.blocks : [];
}

/** The list a block sits in (the document's, or a column's), and its index there. */
function siblings(body: Json, path: BlockPath): { list: unknown[]; index: number } | null {
  if (path.length === 1) return { list: topBlocks(body), index: path[0]! };
  if (path.length !== 3) return null;
  const parent = topBlocks(body)[path[0]!];
  if (!isRecord(parent) || !Array.isArray(parent.columns)) return null;
  const column = parent.columns[path[1]!];
  if (!isRecord(column) || !Array.isArray(column.blocks)) return null;
  return { list: column.blocks, index: path[2]! };
}

export function blockAt(body: Json, path: BlockPath): Json | null {
  const found = siblings(body, path);
  const block = found?.list[found.index];
  return isRecord(block) ? block : null;
}

/** The document with its list at `path`'s level rewritten; the rest shared, never mutated. */
function withSiblings(body: Json, path: BlockPath, write: (list: unknown[]) => unknown[]): Json {
  if (path.length === 1) return { ...body, blocks: write([...topBlocks(body)]) };
  const blocks = [...topBlocks(body)];
  const parent = blocks[path[0]!] as Json;
  const columns = [...(parent.columns as unknown[])];
  const column = columns[path[1]!] as Json;
  columns[path[1]!] = { ...column, blocks: write([...(column.blocks as unknown[])]) };
  blocks[path[0]!] = { ...parent, columns };
  return { ...body, blocks };
}

export function replaceBlock(body: Json, path: BlockPath, block: Json): Json {
  if (blockAt(body, path) === null) return body;
  return withSiblings(body, path, (list) => {
    list[path.at(-1)!] = block;
    return list;
  });
}

/** Whether a block can move one place `direction` within the list it sits in. */
export function canMove(body: Json, path: BlockPath, direction: -1 | 1): boolean {
  const found = siblings(body, path);
  if (found === null) return false;
  const to = found.index + direction;
  return to >= 0 && to < found.list.length;
}

export function moveBlock(body: Json, path: BlockPath, direction: -1 | 1): Json {
  if (!canMove(body, path, direction)) return body;
  return withSiblings(body, path, (list) => {
    const from = path.at(-1)!;
    const [block] = list.splice(from, 1);
    list.splice(from + direction, 0, block);
    return list;
  });
}

/**
 * The document without the block. A column's last block goes with its whole `columns` block, which can hold
 * no empty column.
 */
export function deleteBlock(body: Json, path: BlockPath): Json {
  const found = siblings(body, path);
  if (found === null || blockAt(body, path) === null) return body;
  if (path.length === 3 && found.list.length === 1) return deleteBlock(body, [path[0]!]);
  return withSiblings(body, path, (list) => {
    list.splice(path.at(-1)!, 1);
    return list;
  });
}

/** What a person may do to a block of this kind by hand. An `html` block is the AI's alone. */
export function handEditable(kind: string | null): {
  words: boolean;
  move: boolean;
  remove: boolean;
  swapImage: boolean;
} {
  if (kind === 'html' || kind === null) {
    return { words: false, move: false, remove: false, swapImage: false };
  }
  return {
    words: kind === 'heading' || kind === 'paragraph' || kind === 'list' || kind === 'button',
    move: true,
    remove: true,
    swapImage: kind === 'image',
  };
}

// ── Inline content ⇄ the editable DOM ────────────────────────────────────────────────────────────────

export type Mark = 'bold' | 'italic';

export type LinkTarget =
  | { kind: 'appeal' }
  | { kind: 'appeal_join' }
  | { kind: 'url'; url: string }
  | { kind: 'email'; address: string }
  | { kind: 'phone'; number: string };

export type InlineNode =
  | { type: 'text'; text: string; marks?: Mark[]; link?: LinkTarget | null }
  | { type: 'merge_field'; field: 'first_name'; fallback: string; marks?: Mark[] };

/** The editable DOM's marks on a link and a merge field: what each one was, so it comes back whole. */
export const HAND_LINK_ATTR = 'data-hand-link';
export const HAND_MERGE_ATTR = 'data-hand-merge';
export const HAND_FALLBACK_ATTR = 'data-hand-fallback';

/** A block's inline content as a list, tolerantly: anything unreadable is left out. */
export function inlineContent(value: unknown): InlineNode[] {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (node): node is InlineNode =>
      isRecord(node) &&
      ((node.type === 'text' && typeof node.text === 'string') ||
        (node.type === 'merge_field' && typeof node.fallback === 'string')),
  );
}

function marksOf(node: { marks?: unknown }): Mark[] {
  return Array.isArray(node.marks)
    ? node.marks.filter((mark): mark is Mark => mark === 'bold' || mark === 'italic')
    : [];
}

/**
 * Draw `content` into `into` (its children replaced) as the editor edits it: a run's bold and italic as
 * `<strong>` and `<em>`, its link as an `<a>` carrying the link's target (never an `href`: the preview is not
 * a way out of the console), a line break as `<br>`, and the merge field as its "First name" chip, which is
 * one piece the caret steps over.
 */
export function drawInline(
  doc: Document,
  into: HTMLElement,
  content: readonly InlineNode[],
  options: { chipLabel: string; linkColor?: string } = { chipLabel: 'First name' },
): void {
  const parts: Node[] = [];
  for (const node of content) {
    let inner: Node;
    if (node.type === 'merge_field') {
      inner = mergeChip(doc, node.fallback, options.chipLabel);
    } else {
      const fragment = doc.createDocumentFragment();
      node.text.split('\n').forEach((line, index) => {
        if (index > 0) fragment.append(doc.createElement('br'));
        if (line.length > 0) fragment.append(doc.createTextNode(line));
      });
      inner = fragment;
      // The server writes `link: null` on a run with none.
      if (node.link != null) {
        const link = doc.createElement('a');
        link.setAttribute(HAND_LINK_ATTR, JSON.stringify(node.link));
        link.style.cssText = `color:${options.linkColor ?? 'inherit'};text-decoration:underline;`;
        link.append(inner);
        inner = link;
      }
    }
    const marks = marksOf(node);
    if (marks.includes('italic')) {
      const em = doc.createElement('em');
      em.append(inner);
      inner = em;
    }
    if (marks.includes('bold')) {
      const strong = doc.createElement('strong');
      strong.append(inner);
      inner = strong;
    }
    parts.push(inner);
  }
  into.replaceChildren(...parts);
}

/** The merge field's chip, as the preview draws it (`drawFirstNameChips`), carrying its fallback. */
export function mergeChip(doc: Document, fallback: string, label: string): HTMLElement {
  const chip = doc.createElement('span');
  chip.setAttribute(HAND_MERGE_ATTR, 'first_name');
  chip.setAttribute(HAND_FALLBACK_ATTR, fallback);
  chip.setAttribute('data-designer-merge-field', 'first_name');
  chip.setAttribute('contenteditable', 'false');
  chip.style.cssText =
    'display:inline-block;padding:0 4px;margin:0 1px;border-radius:4px;background:#e7edff;color:#3b5bdb;font-size:0.82em;font-weight:600;line-height:1.55;vertical-align:baseline;';
  chip.textContent = label;
  return chip;
}

function readLink(element: Element): LinkTarget | undefined {
  const raw = element.getAttribute(HAND_LINK_ATTR);
  if (raw === null) return undefined;
  try {
    const value: unknown = JSON.parse(raw);
    return isRecord(value) && typeof value.kind === 'string' ? (value as LinkTarget) : undefined;
  } catch {
    return undefined;
  }
}

function sameLink(a: LinkTarget | null | undefined, b: LinkTarget | null | undefined): boolean {
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}

function sameMarks(a: readonly Mark[], b: readonly Mark[]): boolean {
  return a.length === b.length && a.every((mark) => b.includes(mark));
}

/**
 * Read the edited DOM back into inline content: text and line breaks into runs, `<strong>`/`<b>` (or a
 * bold weight a browser's own bold wrote) as bold, `<em>`/`<i>` as italic, a link by the target it carries,
 * and the chip as the merge field with its fallback. Adjacent runs alike are joined, a trailing line break
 * (a browser adds one to keep an empty line) dropped. Order of marks is fixed: bold, then italic.
 */
export function readInline(root: HTMLElement): InlineNode[] {
  const out: InlineNode[] = [];
  const push = (text: string, marks: Mark[], link: LinkTarget | undefined) => {
    if (text.length === 0) return;
    const last = out.at(-1);
    if (
      last !== undefined &&
      last.type === 'text' &&
      sameMarks(marksOf(last), marks) &&
      sameLink(last.link, link)
    ) {
      last.text += text;
      return;
    }
    const run: InlineNode = { type: 'text', text };
    if (marks.length > 0) run.marks = [...marks];
    if (link !== undefined) run.link = link;
    out.push(run);
  };
  const walk = (node: Node, marks: Mark[], link: LinkTarget | undefined, block: boolean) => {
    node.childNodes.forEach((child, index) => {
      if (child.nodeType === 3) {
        push((child as Text).data.replace(/\u00a0/g, ' ').replace(/\r?\n/g, ' '), marks, link);
        return;
      }
      if (child.nodeType !== 1) return;
      const element = child as HTMLElement;
      const tag = element.tagName.toLowerCase();
      if (element.hasAttribute(HAND_MERGE_ATTR)) {
        const fallback = (element.getAttribute(HAND_FALLBACK_ATTR) ?? '').trim();
        const merge: InlineNode = {
          type: 'merge_field',
          field: 'first_name',
          fallback: fallback.length > 0 ? fallback : 'there',
        };
        if (marks.length > 0) merge.marks = [...marks];
        out.push(merge);
        return;
      }
      if (tag === 'br') {
        push('\n', marks, link);
        return;
      }
      let next = marks;
      const weight = element.style.fontWeight;
      if (tag === 'strong' || tag === 'b' || weight === 'bold' || Number(weight) >= 600) {
        next = next.includes('bold') ? next : [...next, 'bold'];
      }
      if (weight === 'normal' || (weight !== '' && Number(weight) > 0 && Number(weight) < 600)) {
        next = next.filter((mark) => mark !== 'bold');
      }
      if (tag === 'em' || tag === 'i' || element.style.fontStyle === 'italic') {
        next = next.includes('italic') ? next : [...next, 'italic'];
      }
      if (element.style.fontStyle === 'normal') next = next.filter((mark) => mark !== 'italic');
      next = (['bold', 'italic'] as const).filter((mark) => next.includes(mark));
      const nextLink = tag === 'a' ? readLink(element) : link;
      // A browser's own new line inside the editor is a `<div>` or `<p>`: a line break before it.
      const isBlock = tag === 'div' || tag === 'p';
      if (isBlock && (index > 0 || block) && out.length > 0) push('\n', marks, link);
      walk(element, next, nextLink, isBlock);
    });
  };
  walk(root, [], undefined, false);
  // A trailing line break, and whitespace at either end of the whole, is never the person's.
  const last = out.at(-1);
  if (last?.type === 'text') {
    last.text = last.text.replace(/[\s\n]+$/, '');
    if (last.text.length === 0) out.pop();
  }
  const first = out[0];
  if (first?.type === 'text') {
    first.text = first.text.replace(/^\s+/, '');
    if (first.text.length === 0) out.shift();
  }
  return out;
}

/** Whether two inline contents say the same: the editor saves nothing when nothing changed. */
export function sameInline(a: readonly InlineNode[], b: readonly InlineNode[]): boolean {
  const canon = (nodes: readonly InlineNode[]) =>
    JSON.stringify(
      nodes.map((node) =>
        node.type === 'text'
          ? { t: node.text, m: [...marksOf(node)].sort(), l: node.link ?? null }
          : { f: node.fallback, m: [...marksOf(node)].sort() },
      ),
    );
  return canon(a) === canon(b);
}

/** The words of inline content, for a block's plain check (is there anything left?). */
export function inlineWords(content: readonly InlineNode[]): string {
  return content.map((node) => (node.type === 'text' ? node.text : node.fallback)).join('');
}

// ── Links ──────────────────────────────────────────────────────────────────────────────────────────

/**
 * What a person typed as a link's address, as the document writes it: a web page (`https://…`, and a bare
 * `www.` or domain read as https), an email address (`mailto:` or a bare `name@domain`) or a phone number
 * (`tel:`, or digits with the separators people type). Null when it is none of them; the server, which
 * refuses what an email can't carry, stays the authority.
 */
export function parseLinkInput(input: string): LinkTarget | null {
  const value = input.trim();
  if (value.length === 0 || value.length > 2000 || /[<>"]/.test(value)) return null;
  if (/^https?:\/\/[^\s/]+\.\S+$/i.test(value)) return { kind: 'url', url: value };
  if (/^mailto:/i.test(value)) {
    const address = value.slice('mailto:'.length);
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address) ? { kind: 'email', address } : null;
  }
  if (/^[^\s@/]+@[^\s@/]+\.[^\s@/]+$/.test(value)) return { kind: 'email', address: value };
  const phone = value.replace(/^tel:/i, '');
  if (/^\+?\(?\d[\d ().-]{1,28}\d$/.test(phone) && phone.replace(/\D/g, '').length >= 3) {
    return { kind: 'phone', number: phone };
  }
  if (/^(www\.)?[a-z0-9-]+(\.[a-z0-9-]+)+(\/\S*)?$/i.test(value)) {
    return { kind: 'url', url: `https://${value}` };
  }
  return null;
}

/** How a link's target reads in its field: the address a person typed, or null for the appeal's pages. */
export function linkInputText(link: LinkTarget | undefined): string {
  if (link === undefined) return '';
  if (link.kind === 'url') return link.url;
  if (link.kind === 'email') return link.address;
  if (link.kind === 'phone') return link.number;
  return '';
}

/**
 * The page's history key, or null: Cmd/Ctrl+Z is Undo, Shift with it (or Ctrl+Y) is Redo. Null while the
 * key lands in something a person types in (a field, or words open for editing), where the browser's own undo
 * is that field's, and inside a dialog, whose acts aren't the email's. The event may come from the preview's
 * frame, another window, so nothing here is an `instanceof`.
 */
export function historyKeyOf(event: {
  key: string;
  metaKey: boolean;
  ctrlKey: boolean;
  shiftKey: boolean;
  altKey: boolean;
  target: EventTarget | null;
}): 'undo' | 'redo' | null {
  if (!(event.metaKey || event.ctrlKey) || event.altKey) return null;
  const key = event.key.toLowerCase();
  const which =
    key === 'z' ? (event.shiftKey ? 'redo' : 'undo') : key === 'y' && event.ctrlKey ? 'redo' : null;
  if (which === null) return null;
  const target = event.target as
    | (Partial<HTMLElement> & { closest?: (selector: string) => Element | null })
    | null;
  if (target?.isContentEditable === true) return null;
  if (target?.closest?.('input, textarea, select, [contenteditable], [role="dialog"]')) return null;
  return which;
}

/** Where a link goes, as the bar says it: its address as typed, or the appeal's page in `words`. */
export function linkAddress(link: LinkTarget, words: { appeal: string; join: string }): string {
  if (link.kind === 'appeal') return words.appeal;
  if (link.kind === 'appeal_join') return words.join;
  return linkInputText(link);
}
